import {
  getBalanceFromAccountState,
  getBalanceFromTxLog,
  getUnconfirmedReceivedBalance,
  getUnconfirmedSentBalance,
} from '@exodus/asset-lib'
import { isRpcBalanceAsset } from '@exodus/ethereum-lib'
import assert from 'minimalistic-assert'

const getStaked = ({ accountState, asset }) => {
  return accountState?.staking?.[asset.name]?.delegatedBalance || asset.currency.ZERO
}

const getStaking = ({ accountState, asset }) => {
  return (accountState?.staking?.[asset.name]?.pendingBalance || asset.currency.ZERO).add(
    accountState?.staking?.[asset.name]?.pendingDepositedBalance || asset.currency.ZERO
  )
}

const getUnclaimedUndelegatedBalance = ({ accountState, asset }) => {
  return accountState?.staking?.[asset.name]?.unclaimedUndelegatedBalance || asset.currency.ZERO
}

const getCanClaimUndelegatedBalance = ({ accountState, asset }) =>
  accountState?.staking?.[asset.name]?.canClaimUndelegatedBalance || false

/**
 * Calculates amount received in staking txs from txLog
 * Staking txs are counted based in the amount received in the tx (tx.coinAmount)
 * It can be different from staked / unstaked / claimed requested amount)
 *
 * Unstaked tx.coinAmount - Staked tx.coinAmount + Claimed tx.coinAmount
 *
 * Depending on the asset, is what tx.coinAmount is:
 *
 * Ethereum: for unstake txs, it's unknown how much of it are rewards and unstaked received (full or partial)
 * Polygon: rewards are transferred in stake and unstake txs
 */
export const getStakingHistoryBalance = ({ asset, txLog }) => {
  let stakingHistoryBalance = asset.currency.ZERO
  for (const tx of txLog) {
    const successfulTx = tx.confirmations && !tx.failed
    const txData = tx.data

    // staking tx must has data value
    if (!txData) continue

    let { txAmount } = txData
    if (successfulTx && (txAmount || txData.delegate)) {
      // only staking txs have tx.data.txAmount set

      if (txData.delegate && !txAmount) {
        txAmount = txData.delegate // fix missing txAmount in stakeTx
      }

      stakingHistoryBalance = stakingHistoryBalance.add(asset.currency.defaultUnit(txAmount))
    }
  }

  return stakingHistoryBalance
}

const getSpendable = ({ asset, balance, txLog, unconfirmedReceived }) => {
  const stakingHistoryBalance = getStakingHistoryBalance({ asset, txLog })
  return balance.add(stakingHistoryBalance).sub(unconfirmedReceived)
}

/**
 * Api method to return the balance based on either account state balances or tx history.
 *
 * @param asset the asset to get the balances
 * @param txLog the txLog when the balance is transaction based
 * @param accountState the account state when the balance is loaded from RPC
 * @returns {{balance}|null} an object with the balance or null if the balance is unknown
 */
export const getBalancesFactory = ({ monitorType, config = Object.create(null) }) => {
  const { useAccountStateBalanceOnly } = config

  assert(monitorType, 'monitorType is required')
  return ({ asset, txLog, accountState }) => {
    const unconfirmedReceived = getUnconfirmedReceivedBalance({ asset, txLog })
    const unconfirmedSent = getUnconfirmedSentBalance({ asset, txLog })

    const staked = getStaked({ asset, accountState })
    const staking = getStaking({ asset, accountState })

    const canClaimUndelegatedBalance = getCanClaimUndelegatedBalance({ accountState, asset })

    const unclaimedUndelegatedBalance = getUnclaimedUndelegatedBalance({ asset, accountState })

    const unstaking = canClaimUndelegatedBalance ? asset.currency.ZERO : unclaimedUndelegatedBalance
    const unstaked = canClaimUndelegatedBalance ? unclaimedUndelegatedBalance : asset.currency.ZERO

    let total
    let spendable

    // Balance from accountState is considered total b/c is fetched from rpc
    if (useAccountStateBalanceOnly || isRpcBalanceAsset(asset) || monitorType === 'no-history') {
      total = getBalanceFromAccountState({ asset, accountState }).sub(unconfirmedSent)
      spendable = total.sub(staked).sub(staking).sub(unstaking).sub(unstaked)
    } else {
      // Balance from txLog does not include staking rewards
      // spendable and total are calculated differently based on staking txs
      spendable = getSpendable({
        asset,
        balance: getBalanceFromTxLog({ txLog, asset }),
        txLog,
        unconfirmedReceived,
      })
      total = spendable.add(staked).add(staking).add(unstaking).add(unstaked)
    }

    return {
      // new
      spendable,
      total,
      staked,
      staking,
      unstaking,
      unstaked,
      unconfirmedReceived,
      unconfirmedSent,
      // legacy
      balance: total,
      spendableBalance: spendable,
    }
  }
}
